import { useEffect, useState } from 'react';

import { getClientInsightById } from 'api/generated/client-insights/client-insights';
import type { InsightDto } from 'api/generated/schemas/insightDto';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type InsightDetail = InsightDto;

interface UseInsightDetailsArgs {
  clientId: string | number | undefined;
  insightId: string | number | undefined;
}

interface UseInsightDetailsResult {
  insight: InsightDetail | null;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

export const useInsightDetails = ({
  clientId,
  insightId,
}: UseInsightDetailsArgs): UseInsightDetailsResult => {
  const [insight, setInsight] = useState<InsightDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const loadInsight = async (): Promise<void> => {
    if (!userId || !clientId || !insightId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getClientInsightById(
        userId,
        Number(clientId),
        Number(insightId),
      );

      setInsight(data ?? null);
    } catch (err) {
      console.error('Error loading insight details:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsight();
  }, [userId, clientId, insightId]);

  return {
    insight,
    loading,
    error,
    refetch: loadInsight,
  };
};
